/**
 * Uji produksi sebuah lapangan: apa yang benar-benar keluar dari tiap selang.
 *
 * Satu-satunya data di aplikasi ini yang bukan pengukuran melainkan kesaksian,
 * dan satu-satunya yang memisahkan "LRLC potential" dari "LRLC proven" — sebab
 * kurvanya membaca sama persis pada keduanya.
 *
 * Panelnya berdiri di dalam layar LRLC dan bukan di pengelola data, dengan
 * sengaja: yang menyadari sebuah uji belum tercatat adalah orang yang sedang
 * menatap lapisan yang tidak bisa dinaikkan tanpa uji itu. Menyuruhnya pindah
 * layar berarti menyuruhnya kembali nanti, dan nanti tidak pernah datang.
 */
import { useState } from "react";
import { api } from "../api";
import { canWrite } from "../auth";
import {
  Button,
  Dropdown,
  Empty,
  Loading,
  Notice,
  NumberField,
  TextField,
  useToast,
} from "./ui";
import { useAsync } from "../hooks";
import { count } from "../lib/plural";
import type { ProductionTest, Project, Well } from "../types";

const FLUIDS: { value: ProductionTest["fluid"]; label: string }[] = [
  { value: "oil", label: "Oil" },
  { value: "gas", label: "Gas" },
  { value: "oil_gas", label: "Oil and gas" },
  { value: "water", label: "Water" },
  { value: "dry", label: "Dry" },
];

const KINDS: { value: ProductionTest["kind"]; label: string }[] = [
  { value: "dst", label: "DST" },
  { value: "rft", label: "RFT / MDT" },
  { value: "perforation", label: "Perforation" },
  { value: "production", label: "Production" },
];

/** Warna tiap fluida. Hidrokarbon hangat, air dingin, kering kelabu. */
const FLUID_COLOR: Record<ProductionTest["fluid"], string> = {
  oil: "#2e8b57",
  gas: "#d1342f",
  oil_gas: "#c2185b",
  water: "#00bfff",
  dry: "#8b939e",
};

const cell: React.CSSProperties = {
  padding: "5px 9px", textAlign: "right",
  font: "400 10.5px var(--font-mono)",
  borderBottom: "1px solid var(--line-soft)", whiteSpace: "nowrap",
};
const head: React.CSSProperties = {
  ...cell, font: "600 9.5px var(--font-mono)", color: "var(--ink-5)",
  borderBottom: "1px solid var(--line-strong)",
  position: "sticky", top: 0, background: "var(--panel)",
};
const left: React.CSSProperties = { textAlign: "left" };

export default function ProductionTests({
  project,
  wells,
  version = 0,
  onChanged,
}: {
  project: Project;
  wells: Well[];
  version?: number;
  onChanged?: () => void;
}) {
  const toast = useToast();
  const mayWrite = canWrite(project.my_role);
  const tests = useAsync(
    (signal) => api
      .get<{ results: ProductionTest[] }>("/api/production-tests/",
                                          { project: project.id }, signal)
      .then((page) => page.results),
    [project.id, version],
  );

  const [wellId, setWellId] = useState<number | null>(null);
  const [top, setTop] = useState<number>(0);
  const [base, setBase] = useState<number>(0);
  const [fluid, setFluid] = useState<ProductionTest["fluid"]>("oil");
  const [kind, setKind] = useState<ProductionTest["kind"]>("dst");
  const [rate, setRate] = useState("");
  const [busy, setBusy] = useState(false);

  const well = wellId ?? wells[0]?.id ?? null;

  const add = async () => {
    if (well === null) return;
    setBusy(true);
    try {
      await api.post("/api/production-tests/", {
        well, top_md: top, base_md: base, fluid, kind, rate,
      });
      toast("Production test recorded.");
      setRate("");
      tests.reload();
      onChanged?.();
    } catch (cause) {
      toast(cause instanceof Error ? cause.message : String(cause), "error");
    } finally {
      setBusy(false);
    }
  };

  const remove = async (test: ProductionTest) => {
    try {
      await api.delete(`/api/production-tests/${test.id}/`);
      tests.reload();
      onChanged?.();
    } catch (cause) {
      toast(cause instanceof Error ? cause.message : String(cause), "error");
    }
  };

  if (tests.loading && !tests.data) return <Loading label="Reading the tests…" />;
  const rows = tests.data ?? [];

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 9 }}>
      <Notice tone="info">
        What a curve cannot say. A low-resistivity interval that behaves like
        pay stays <b>potential</b> until a test on that depth returns
        hydrocarbon — and then, on exactly the same curves, it reads
        <b> proven</b>.
      </Notice>

      {/* Kisi dan bukan flex-wrap. Keenam kotaknya punya tinggi yang berbeda —
          `Dropdown` menaruh namanya di sebelah kotaknya, `NumberField` di
          atasnya — dan flex-wrap pada lebar yang sempit menumpuk keduanya
          sampai nama kotak terakhir tercetak di atas kotak sebelahnya. Kisi
          memberi tiap kotak petaknya sendiri, dan petak yang tidak muat turun
          utuh ke baris berikutnya. */}
      {mayWrite && (
        <div style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(148px, 1fr))",
          gap: 8, alignItems: "end",
          padding: 9, border: "1px solid var(--line)", borderRadius: 7,
          background: "var(--panel-inset)",
        }}>
          <div style={{ minWidth: 0 }}>
            <Dropdown
              label="Well"
              value={well ?? 0}
              options={wells.map((w) => ({ value: w.id, label: w.name }))}
              onChange={(value) => setWellId(Number(value))}
            />
          </div>
          <div style={{ minWidth: 0 }}>
            <NumberField label="Top MD" unit="m" value={top} step={1} onChange={setTop} />
          </div>
          <div style={{ minWidth: 0 }}>
            <NumberField label="Base MD" unit="m" value={base} step={1} onChange={setBase} />
          </div>
          <div style={{ minWidth: 0 }}>
            <Dropdown label="Produced" value={fluid} options={FLUIDS}
                      onChange={(value) => setFluid(value as ProductionTest["fluid"])} />
          </div>
          <div style={{ minWidth: 0 }}>
            <Dropdown label="Test" value={kind} options={KINDS}
                      onChange={(value) => setKind(value as ProductionTest["kind"])} />
          </div>
          <div style={{ minWidth: 0 }}>
            <TextField label="Rate" value={rate} onChange={setRate}
                       placeholder="420 bopd" />
          </div>
          <Button
            variant="primary"
            busy={busy}
            onClick={add}
            disabled={well === null || base <= top}
            title={base <= top
              ? "Base must be deeper than top"
              : "Record what this interval produced"}
          >
            Record the test
          </Button>
        </div>
      )}

      {rows.length === 0 ? (
        <Empty>
          No production test recorded on this field yet, so nothing can be
          proven — every low-resistivity pay stays potential.
        </Empty>
      ) : (
        <>
          <div style={{ border: "1px solid var(--line)", borderRadius: 7, overflow: "hidden" }}>
            <table style={{ width: "100%", borderCollapse: "collapse", background: "var(--panel-inset)" }}>
              <thead>
                <tr>
                  <th style={{ ...head, ...left }}>Well</th>
                  <th style={head}>Top m</th>
                  <th style={head}>Base m</th>
                  <th style={{ ...head, ...left }}>Produced</th>
                  <th style={{ ...head, ...left }}>Test</th>
                  <th style={{ ...head, ...left }}>Rate</th>
                  {mayWrite && <th style={head} />}
                </tr>
              </thead>
              <tbody>
                {rows.map((test) => (
                  <tr key={test.id}>
                    <td style={{ ...cell, ...left, color: "var(--ink-2)" }}>{test.well_name}</td>
                    <td style={cell}>{test.top_md.toFixed(1)}</td>
                    <td style={cell}>{test.base_md.toFixed(1)}</td>
                    <td style={{ ...cell, ...left }}>
                      <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
                        <span style={{
                          width: 8, height: 8, borderRadius: 2, flex: "none",
                          background: FLUID_COLOR[test.fluid],
                        }} />
                        {test.fluid_label}
                      </span>
                    </td>
                    <td style={{ ...cell, ...left, color: "var(--ink-5)" }}>{test.kind_label}</td>
                    <td style={{ ...cell, ...left, color: "var(--ink-5)" }}>{test.rate || "—"}</td>
                    {mayWrite && (
                      <td style={cell}>
                        <button
                          type="button"
                          onClick={() => void remove(test)}
                          title={`Delete this test on ${test.well_name}`}
                          style={{
                            background: "none", border: "none", cursor: "pointer",
                            color: "var(--ink-6)", font: "400 12px var(--font-mono)",
                          }}
                        >
                          ×
                        </button>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <span style={{ font: "400 10px/1.6 var(--font-sans)", color: "var(--ink-6)" }}>
            {count(rows.filter((t) => t.is_hydrocarbon).length, "test")} returned
            hydrocarbon, of {count(rows.length, "test")} on this field. Where two
            tests overlap, hydrocarbon wins — a re-perforation inside a wider
            water test is a thing that really happens, and what came up on the
            overlap was still oil.
          </span>
        </>
      )}
    </div>
  );
}
